// Leveled logging for the web frontend, modelled on swift-log
// (Sources/LogbookFoundation/Logger.swift). All console output should go
// through `logger` (or a categorised child via `createLogger`) rather than
// calling `console.*` directly, so verbosity is controlled centrally.
import { androidBridge, appleBridge, bridgeFor, windowsBridge } from "./Bridge"
import { serializeLogValue } from "app-base/runtime/logger"
import {
  AppleBridgeManifestRequests,
  AndroidBridgeManifestRequests,
  WindowsBridgeManifestRequests,
} from "../Types/Generated/Bridge/NativeBridge"

//
// By default debug and higher levels reach the console and native bridge.
// To change the level at runtime, run this in the browser devtools console:
//
//     logbook.setLogLevel("debug")   // or "trace", "info", ...
//
// The chosen level is persisted in localStorage, so it survives reloads. Reset
// with `logbook.setLogLevel("debug")`. In Node/tests the level also honours
// the `VERBOSE` / `LOG_LEVEL` environment variables.
//
// On native (iOS/Mac/Windows/Android) builds, emitted messages are also
// mirrored, with their level, to the native log bridge (captured into the
// shell's capped local log file -- see issue #405). Pass the `NATIVE` sentinel
// as the last argument to force a message to native even when the level hides
// it from the console (e.g. `logger.debug("session load error", err, NATIVE)`),
// or call `logger.native(...)` to log only to native (defaulting to "info").

export type LogLevel =
  | "trace"
  | "debug"
  | "info"
  | "notice"
  | "warning"
  | "error"
  | "critical"
  | "none";

// Sentinel appended as the FINAL argument to a log call to force the message to
// the native bridge even when the current level would otherwise suppress it.
// Console output still respects the level. Used where `app.logNative` was
// previously called, e.g. `logger.debug("session load error", err, NATIVE)`.
export const NATIVE: unique symbol = Symbol("logbook.native");

// Ordered by increasing severity; `none` disables all output.
const RANK: Record<LogLevel, number> = {
  trace: 0,
  debug: 1,
  info: 2,
  notice: 3,
  warning: 4,
  error: 5,
  critical: 6,
  none: 7,
};

// Which console method each level is routed to when it passes the threshold.
const CONSOLE_METHOD: Record<Exclude<LogLevel, "none">, "debug" | "info" | "warn" | "error"> = {
  trace: "debug",
  debug: "debug",
  info: "info",
  notice: "info",
  warning: "warn",
  error: "error",
  critical: "error",
};

const STORAGE_KEY = "logbook:logLevel";
export const DEFAULT_LOG_LEVEL: LogLevel = "debug";

// The generated native manifests are preferred. Legacy hosts then use iOS/Mac
// `webkit.messageHandlers.log`, Windows `chrome.webview` (tagged with a "log"
// type discriminator, since it's one shared channel), or Android
// `__logbookLog` (a JS interface backed by pure Java). Every path carries the same
// `{level, message}` payload (Windows nests it inside `{type: "log", ...}`) so the
// native side can route it into the same capped log file regardless of shell
// (see issue #405).
//
// `message` rather than `text` because app-base's shared log channel names it that,
// and the two converged when app-base renamed `print` to `log`. Note that
// `__logbookPrint` above is PAPER printing, not logging, and is unrelated to either.
type NativeBridge = {
  webkit?: { messageHandlers?: { log?: { postMessage: (data: string) => unknown } } };
  chrome?: { webview?: { postMessage: (data: string) => void } };
  __logbookLog?: { log: (level: string, text: string) => void };
};

function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return serializeLogValue(value);
  } catch {
    return String(value);
  }
}

function postToNative(prefix: string | undefined, level: Exclude<LogLevel, "none">, args: unknown[]): void {
  try {
    const text = args.map(safeStringify).join(" ");
    const fullText = prefix ? `${prefix} ${text}` : text;
    const apple = appleBridge();
    if (apple?.supports(AppleBridgeManifestRequests.log)) {
      void apple.post(
        AppleBridgeManifestRequests.log,
        { level, message: fullText },
      ).catch(() => {});
      return;
    }
    const android = androidBridge();
    if (android?.supports(AndroidBridgeManifestRequests.log)) {
      void android.post(
        AndroidBridgeManifestRequests.log,
        { level, message: fullText },
      ).catch(() => {});
      return;
    }
    const windows = windowsBridge();
    if (windows?.supports(WindowsBridgeManifestRequests.log)) {
      void windows.post(
        WindowsBridgeManifestRequests.log,
        { level, message: fullText },
      ).catch(() => {});
      return;
    }
    const unified = bridgeFor("log");
    if (unified) {
      void unified.post("log", { level, message: fullText }).catch(() => {});
      return;
    }
    const bridge = globalThis as NativeBridge;
    const log = bridge.webkit?.messageHandlers?.log;
    if (log) {
      log.postMessage(JSON.stringify({ level, message: fullText }));
      return;
    }
    if (bridge.chrome?.webview) {
      bridge.chrome.webview.postMessage(JSON.stringify({ type: "log", level, message: fullText }));
      return;
    }
    if (bridge.__logbookLog) {
      bridge.__logbookLog.log(level, fullText);
      return;
    }
  } catch {
    // Native bridge unavailable or postMessage threw — ignore.
  }
}

function isLogLevel(value: unknown): value is LogLevel {
  return typeof value === "string" && value in RANK;
}

function readStoredLevel(): LogLevel | undefined {
  try {
    const store = (globalThis as { localStorage?: Storage }).localStorage;
    const value = store?.getItem?.(STORAGE_KEY);
    if (isLogLevel(value)) {
      return value;
    }
  } catch {
    // localStorage may be unavailable (SSR, disabled cookies) — ignore.
  }
  return undefined;
}

export function resolveEnvironmentLogLevel(
  env: Record<string, string | undefined>,
): LogLevel | undefined {
  const level = env.LOG_LEVEL?.toLowerCase();
  if (isLogLevel(level)) return level;
  if (env.VERBOSE === "2") return "debug";
  if (env.VERBOSE) return "info";
  return undefined;
}

function readEnvLevel(): LogLevel | undefined {
  try {
    const env = typeof process !== "undefined" ? process.env : undefined;
    if (env) return resolveEnvironmentLogLevel(env);
  } catch {
    // `process` is not defined in some browser bundles — ignore.
  }
  return undefined;
}

let currentLevel: LogLevel = readStoredLevel() ?? readEnvLevel() ?? DEFAULT_LOG_LEVEL;

export function getLogLevel(): LogLevel {
  return currentLevel;
}

export function setLogLevel(level: LogLevel): LogLevel {
  if (!isLogLevel(level)) {
    // Use console.warn directly so the guidance is always visible.
    console.warn(`[logbook] invalid log level "${level}". Valid levels: ${Object.keys(RANK).join(", ")}`);
    return currentLevel;
  }
  currentLevel = level;
  try {
    (globalThis as { localStorage?: Storage }).localStorage?.setItem?.(STORAGE_KEY, level);
  } catch {
    // Persisting is best-effort.
  }
  // Confirmation is intentionally unconditional so the developer sees it.
  console.log(`[logbook] log level set to "${level}" (persisted in localStorage["${STORAGE_KEY}"]).`);
  return currentLevel;
}

export class Logger {
  constructor(private readonly category?: string) {}

  // Returns a logger that prefixes messages with a (possibly nested) category.
  child(category: string): Logger {
    return new Logger(this.category ? `${this.category}.${category}` : category);
  }

  isEnabled(level: Exclude<LogLevel, "none">): boolean {
    return RANK[level] >= RANK[currentLevel];
  }

  // Logs ONLY to the native bridge (never the console) and ignores the console
  // level. Used internally by `emit` to mirror console output to native (with
  // the log's own level), and available directly for native-only diagnostics --
  // those default to the "info" level, since there's no console call to derive
  // one from.
  native(...args: unknown[]): void {
    this.postNative("info", args);
  }

  private postNative(level: Exclude<LogLevel, "none">, args: unknown[]): void {
    postToNative(this.category ? `[${this.category}]` : undefined, level, args);
  }

  private emit(level: Exclude<LogLevel, "none">, args: unknown[]): void {
    // A trailing NATIVE sentinel forces the message to native even when the
    // level would suppress it (console output still respects the level).
    let forceNative = false;
    if (args.length > 0 && args[args.length - 1] === NATIVE) {
      forceNative = true;
      args = args.slice(0, -1);
    }
    const enabled = RANK[level] >= RANK[currentLevel];
    if (enabled) {
      const method = CONSOLE_METHOD[level];
      if (this.category) {
        console[method](`[${this.category}]`, ...args);
      } else {
        console[method](...args);
      }
    }
    // By default native mirrors the console (only forwards what the level would
    // log); the NATIVE sentinel forces it through regardless. Either way the
    // native envelope carries this call's own level, not the "info" default
    // `native()` uses when called directly.
    if (enabled || forceNative) {
      this.postNative(level, args);
    }
  }

  trace(...args: unknown[]): void {
    this.emit("trace", args);
  }

  debug(...args: unknown[]): void {
    this.emit("debug", args);
  }

  info(...args: unknown[]): void {
    this.emit("info", args);
  }

  notice(...args: unknown[]): void {
    this.emit("notice", args);
  }

  warning(...args: unknown[]): void {
    this.emit("warning", args);
  }

  error(...args: unknown[]): void {
    this.emit("error", args);
  }

  critical(...args: unknown[]): void {
    this.emit("critical", args);
  }
}

// Default application logger.
export const logger = new Logger();

// Create a categorised logger, e.g. `const log = createLogger("Sync")`.
export function createLogger(category: string): Logger {
  return new Logger(category);
}

// Returns the field NAMES of a form `values` object, for logging which fields a
// save submitted without writing the user's content into the device log file.
// `values` is whatever the user typed -- passwords on the signup/change-password
// forms, and their own note/watch text elsewhere -- so it must never be logged
// directly (issue #1273). Nested groups are summarised by their own key alone,
// so nothing below the top level can leak either.
export function fieldNames(values: unknown): string[] {
  if (!values || typeof values !== "object" || Array.isArray(values)) {
    return [];
  }
  return Object.keys(values).sort();
}

// Expose runtime controls on the global object so they can be driven from the
// browser devtools console (e.g. `logbook.setLogLevel("debug")`).
try {
  const globals = globalThis as unknown as { logbook?: Record<string, unknown> };
  globals.logbook = {
    ...(globals.logbook ?? {}),
    logger,
    createLogger,
    getLogLevel,
    setLogLevel,
    levels: Object.keys(RANK) as LogLevel[],
  };
} catch {
  // Ignore environments without a writable global.
}
